// What the AI is told when it reads this company's calls (D-061..D-065): the business in its own words, names to
// spell right, competitors, what hot means here, guidance for cold and conversion calls, a checklist of what a good
// call includes, extra facts to note, and the objection categories. Owners and admins change it; the database checks
// every save (ORG-06..08) and each analysis records which version it used (PIPE-12). Without Coach or Autopilot it is
// locked (D-181).
import { type FormEvent, useState } from "react";
import { Link } from "react-router";
import {
  type AiInstructions,
  type AppliesTo,
  appliesToLabel,
  asBusinessType,
  BUILT_IN_FACTS,
  BUSINESS_TYPES,
  type BusinessType,
  CALL_TYPES,
  callTypeLabel,
  type ChecklistItem,
  checklistJson,
  checklistProblem,
  EXAMPLES,
  type FactField,
  factsJson,
  factsProblem,
  keyFor,
  MAX_CATEGORIES,
  MAX_CHECKLIST,
  MAX_FACTS,
  TEXT_LIMITS,
  type TextField,
  textProblem,
} from "../lib/aiInstructions";
import { fmtDateTime } from "../lib/format";
import type { Member, Taxonomy } from "../lib/types";
import { useMember } from "../app/session";
import { Card, ErrorNote, Field, Loading, Page, useAction, useLoad } from "../app/ui";

export function AiSetup() {
  const { member } = useMember();
  // Without Coach or Autopilot nothing here is used, and the database refuses changes (D-181).
  if (!(member.features?.coach || member.features?.autopilot)) return <AiNotEnabled />;
  return <AiSetupForm />;
}

/** A company with neither Coach nor Autopilot (D-181): the page is locked, and says why. */
function AiNotEnabled() {
  return (
    <Page title="AI instructions">
      <Card>
        <p style={{ margin: 0 }}>AI is not enabled for your organization.</p>
      </Card>
    </Page>
  );
}

function AiSetupForm() {
  const { api, tz } = useMember();
  const { data, error, loading, reload } = useLoad(
    () => Promise.all([api.aiInstructions(), api.objectionCategories(), api.members(), api.org()]),
    [api],
  );
  if (loading && !data) return <Loading />;
  if (!data) return <ErrorNote error={error} />;
  const [ins, categories, members, org] = data;
  const changedBy = members.find((m) => m.id === ins.updated_by)?.display_name;
  const type = asBusinessType(org.vertical);
  const ex = EXAMPLES[type];
  const k = `${ins.version}${type}`;

  return (
    <Page title="AI instructions" sub="What the AI knows when it reads your calls">
      <div className="stack">
        <p className="faint" style={{ margin: 0 }}>
          The AI reads every recorded call with these instructions. Changes apply to calls analysed from now on.{" "}
          {ins.version > 1 && `Last changed ${fmtDateTime(ins.updated_at, tz)}${changedBy ? ` by ${changedBy}` : ""}.`}
        </p>
        <BusinessTypeCard key={`t${type}`} type={type} onSaved={reload} />
        <Business key={`b${k}`} ins={ins} ex={ex} onSaved={reload} />
        <CallTypes key={`c${k}`} ins={ins} ex={ex} members={members} onSaved={reload} />
        <Checklist key={`k${k}`} ins={ins} ex={ex} onSaved={reload} />
        <Facts key={`f${k}`} ins={ins} ex={ex} onSaved={reload} />
        <Categories rows={categories} ex={ex} onChanged={reload} />
      </div>
    </Page>
  );
}

/** A text box with its limit, for one of the company's instructions. */
function Instruction(
  props: {
    field: TextField;
    label: string;
    hint: string;
    rows?: number;
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
  },
) {
  const max = TEXT_LIMITS[props.field];
  return (
    <Field
      label={props.label}
      hint={
        <>
          {props.hint}{" "}
          <span className={props.value.length > max * 0.9 ? "overdue" : ""}>
            {props.value.length.toLocaleString("en-IN")} / {max.toLocaleString("en-IN")}
          </span>
        </>
      }
    >
      <textarea
        rows={props.rows ?? 3}
        maxLength={max}
        value={props.value}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
      />
    </Field>
  );
}

type Examples = (typeof EXAMPLES)[BusinessType];

/** The kind of business (ORG-09): how the AI describes it, its guidance, and the categories and checklist to start from. */
function BusinessTypeCard(props: { type: BusinessType; onSaved: () => Promise<void> }) {
  const { api } = useMember();
  const { busy, run } = useAction();
  const [type, setType] = useState<BusinessType>(props.type);
  const save = async () => {
    if (await run(() => api.setBusinessType(type), "Business type changed")) await props.onSaved();
  };
  return (
    <Card
      title="Business type"
      actions={
        <button className="primary small" disabled={busy || type === props.type} onClick={() => void save()}>
          Save
        </button>
      }
    >
      <p className="faint">
        How the AI describes your business, what it expects of cold and conversion calls, and the objection categories
        and checklist you start from. Changing it adds the new type's categories and hides the old ones that do not fit;
        your own categories, and a checklist you have changed, are kept.
      </p>
      <select value={type} aria-label="Business type" onChange={(e) => setType(e.target.value as BusinessType)}>
        {BUSINESS_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
      </select>
    </Card>
  );
}

function useSave(onSaved: () => Promise<void>) {
  const { api } = useMember();
  const { busy, run } = useAction();
  const save = async (patch: Parameters<typeof api.saveAiInstructions>[0], done = "Saved") => {
    if (await run(() => api.saveAiInstructions(patch), done)) await onSaved();
  };
  return { busy, save };
}

function Business(props: { ins: AiInstructions; ex: Examples; onSaved: () => Promise<void> }) {
  const { busy, save } = useSave(props.onSaved);
  const [v, setV] = useState({
    about: props.ins.about,
    vocabulary: props.ins.vocabulary,
    competitors: props.ins.competitors,
    interest_guide: props.ins.interest_guide,
    extra: props.ins.extra,
  });
  const set = (field: keyof typeof v) => (value: string) => setV((x) => ({ ...x, [field]: value }));
  const problem = (Object.keys(v) as (keyof typeof v)[]).map((f) => textProblem(f, v[f])).find(Boolean) ?? null;
  const dirty = (Object.keys(v) as (keyof typeof v)[]).some((f) => v[f] !== props.ins[f]);

  return (
    <Card
      title="Your business"
      actions={
        <button className="primary small" disabled={busy || !dirty || !!problem} onClick={() => void save(v)}>
          Save
        </button>
      }
    >
      <Instruction
        field="about"
        label="What you sell"
        hint="What you sell, prices, offers, who your customers are. The AI uses it to understand what customers ask about."
        rows={6}
        placeholder={props.ex.about}
        value={v.about}
        onChange={set("about")}
      />
      <div className="grid cols-2">
        <Instruction
          field="vocabulary"
          label="Names the AI should know"
          hint="One per line: product, project or place names, local words. The transcript can mishear them; the AI uses your spelling."
          placeholder={props.ex.vocabulary}
          value={v.vocabulary}
          onChange={set("vocabulary")}
        />
        <Instruction
          field="competitors"
          label="Competitors"
          hint="One per line. When a customer mentions one, it is filed under this name."
          placeholder={props.ex.competitors}
          value={v.competitors}
          onChange={set("competitors")}
        />
      </div>
      <Instruction
        field="interest_guide"
        label="What hot, warm and cold mean for you"
        hint="How the AI should judge a customer's interest. Leave it empty to use Voxowa's own judgement."
        placeholder={props.ex.interest_guide}
        value={v.interest_guide}
        onChange={set("interest_guide")}
      />
      <Instruction
        field="extra"
        label="Anything else"
        hint="Other instructions for the AI. They change what it pays attention to, not the format of what it reports."
        placeholder={props.ex.extra}
        value={v.extra}
        onChange={set("extra")}
      />
      {problem && <div className="error-note">{problem}</div>}
    </Card>
  );
}

function CallTypes(props: { ins: AiInstructions; ex: Examples; members: Member[]; onSaved: () => Promise<void> }) {
  const { busy, save } = useSave(props.onSaved);
  const [cold, setCold] = useState(props.ins.cold_guide);
  const [conversion, setConversion] = useState(props.ins.conversion_guide);
  const active = props.members.filter((m) => m.status === "active" && m.role === "caller");
  const named = (type: string | null) =>
    active.filter((m) => m.call_type === type).map((m) => m.display_name.split(" ")[0]).join(", ") || "nobody";
  const problem = textProblem("cold_guide", cold) ?? textProblem("conversion_guide", conversion);
  const dirty = cold !== props.ins.cold_guide || conversion !== props.ins.conversion_guide;

  return (
    <Card
      title="Cold and conversion calls"
      actions={
        <button
          className="primary small"
          disabled={busy || !dirty || !!problem}
          onClick={() => void save({ cold_guide: cold, conversion_guide: conversion })}
        >
          Save
        </button>
      }
    >
      <p className="faint">
        A cold call is first contact with a new lead; a conversion call moves an interested lead towards booking. The AI
        already judges each kind differently. Set each telecaller's kind of calls on the <Link to="/team">Team</Link>
        {" "}
        page; add here what is particular to your company.
      </p>
      <div className="chips" style={{ marginBottom: 14 }}>
        {CALL_TYPES.map((t) => <span className="badge" key={t}>{callTypeLabel[t]}: {named(t)}</span>)}
        <span className="badge">Both kinds: {named(null)}</span>
      </div>
      <div className="grid cols-2">
        <Instruction
          field="cold_guide"
          label="For cold calls"
          hint="Where these leads come from and what a good first call achieves."
          rows={4}
          placeholder={props.ex.cold_guide}
          value={cold}
          onChange={setCold}
        />
        <Instruction
          field="conversion_guide"
          label="For conversion calls"
          hint="Offers to mention, what it takes to book, what to watch for."
          rows={4}
          placeholder={props.ex.conversion_guide}
          value={conversion}
          onChange={setConversion}
        />
      </div>
      {problem && <div className="error-note">{problem}</div>}
    </Card>
  );
}

function Checklist(props: { ins: AiInstructions; ex: Examples; onSaved: () => Promise<void> }) {
  const { busy, save } = useSave(props.onSaved);
  const [items, setItems] = useState<ChecklistItem[]>(props.ins.checklist);
  const [label, setLabel] = useState("");
  const [applies, setApplies] = useState<AppliesTo>("all");
  const problem = checklistProblem(items);
  const dirty = JSON.stringify(items) !== JSON.stringify(props.ins.checklist);
  const update = (i: number, patch: Partial<ChecklistItem>) =>
    setItems((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const add = (e: FormEvent) => {
    e.preventDefault();
    const text = label.trim();
    if (text.length < 3) return;
    setItems((xs) => [...xs, { key: keyFor(text, xs.map((x) => x.key)), label: text, applies_to: applies }]);
    setLabel("");
  };

  return (
    <Card
      title="Call checklist"
      actions={
        <button
          className="primary small"
          disabled={busy || !dirty || !!problem}
          onClick={() => void save({ checklist: checklistJson(items) }, "Checklist saved")}
        >
          Save
        </button>
      }
    >
      <p className="faint">
        What a good call includes. On every call an item applies to, the AI marks it done, with the caller's own words,
        or missed. The Overview shows how often each person does each item.
      </p>
      <div className="list" style={{ marginBottom: 10 }}>
        {items.map((item, i) => (
          <div className="item-row list-item" key={item.key}>
            <input
              value={item.label}
              maxLength={120}
              aria-label="Checklist item"
              onChange={(e) => update(i, { label: e.target.value })}
            />
            <select
              value={item.applies_to}
              aria-label={`Calls “${item.label}” applies to`}
              onChange={(e) => update(i, { applies_to: e.target.value as AppliesTo })}
            >
              {(["all", ...CALL_TYPES] as AppliesTo[]).map((a) => <option key={a} value={a}>{appliesToLabel[a]}
              </option>)}
            </select>
            <button
              className="small ghost"
              aria-label={`Remove “${item.label}”`}
              onClick={() => setItems((xs) => xs.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="faint">No checklist: calls are not scored.</p>}
      </div>
      {items.length < MAX_CHECKLIST && (
        <form className="item-row" onSubmit={add}>
          <input
            value={label}
            maxLength={120}
            placeholder={props.ex.checklist}
            aria-label="New checklist item"
            onChange={(e) => setLabel(e.target.value)}
          />
          <select
            value={applies}
            aria-label="Calls the new item applies to"
            onChange={(e) => setApplies(e.target.value as AppliesTo)}
          >
            {(["all", ...CALL_TYPES] as AppliesTo[]).map((a) => <option key={a} value={a}>{appliesToLabel[a]}</option>)}
          </select>
          <button className="small" disabled={label.trim().length < 3}>Add</button>
        </form>
      )}
      {problem && <div className="error-note" style={{ marginTop: 10 }}>{problem}</div>}
      {dirty && !problem && <p className="faint" style={{ marginTop: 10 }}>Not saved yet.</p>}
    </Card>
  );
}

function Facts(props: { ins: AiInstructions; ex: Examples; onSaved: () => Promise<void> }) {
  const { busy, save } = useSave(props.onSaved);
  const [facts, setFacts] = useState<FactField[]>(props.ins.facts);
  const [label, setLabel] = useState("");
  const [hint, setHint] = useState("");
  const problem = factsProblem(facts);
  const dirty = JSON.stringify(facts) !== JSON.stringify(props.ins.facts);
  const update = (i: number, patch: Partial<FactField>) =>
    setFacts((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const add = (e: FormEvent) => {
    e.preventDefault();
    const text = label.trim();
    if (text.length < 2) return;
    const taken = [...BUILT_IN_FACTS, ...facts].map((f) => f.key);
    setFacts((
      xs,
    ) => [...xs, { key: keyFor(text, taken, 30), label: text, ...(hint.trim() ? { hint: hint.trim() } : {}) }]);
    setLabel("");
    setHint("");
  };

  return (
    <Card
      title="Facts to note"
      actions={
        <button
          className="primary small"
          disabled={busy || !dirty || !!problem}
          onClick={() => void save({ facts: factsJson(facts) }, "Facts saved")}
        >
          Save
        </button>
      }
    >
      <p className="faint">
        What the AI writes down when a customer says it, kept on the lead. These are always noted:
      </p>
      <div className="chips" style={{ marginBottom: 12 }}>
        {BUILT_IN_FACTS.map((f) => <span className="badge" key={f.key}>{f.label}</span>)}
      </div>
      <div className="list" style={{ marginBottom: 10 }}>
        {facts.map((f, i) => (
          <div className="item-row described list-item" key={f.key}>
            <input
              value={f.label}
              maxLength={60}
              aria-label="Fact"
              onChange={(e) => update(i, { label: e.target.value })}
            />
            <input
              value={f.hint ?? ""}
              maxLength={200}
              placeholder="What to write, e.g. yes or no"
              aria-label={`Hint for “${f.label}”`}
              onChange={(e) => update(i, { hint: e.target.value })}
            />
            <button
              className="small ghost"
              aria-label={`Remove “${f.label}”`}
              onClick={() => setFacts((xs) => xs.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
      {facts.length < MAX_FACTS && (
        <form className="item-row described" onSubmit={add}>
          <input
            value={label}
            maxLength={60}
            placeholder={props.ex.fact}
            aria-label="New fact"
            onChange={(e) => setLabel(e.target.value)}
          />
          <input
            value={hint}
            maxLength={200}
            placeholder={props.ex.factHint}
            aria-label="Hint for the new fact"
            onChange={(e) => setHint(e.target.value)}
          />
          <button className="small" disabled={label.trim().length < 2}>Add</button>
        </form>
      )}
      {problem && <div className="error-note" style={{ marginTop: 10 }}>{problem}</div>}
      {dirty && !problem && <p className="faint" style={{ marginTop: 10 }}>Not saved yet.</p>}
    </Card>
  );
}

function Categories(props: { rows: Taxonomy[]; ex: Examples; onChanged: () => Promise<void> }) {
  const { api } = useMember();
  const { busy, run } = useAction();
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const shown = props.rows.filter((r) => r.active).length;
  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (await run(() => api.addObjectionCategory(label.trim(), description.trim()), "Category added")) {
      setLabel("");
      setDescription("");
      await props.onChanged();
    }
  };

  return (
    <Card title="Objection categories">
      <p className="faint">
        The AI files every objection under one of these, and the Overview counts them. Rename them in your own words,
        hide the ones that never come up, or add your own. Hidden categories keep their past objections.
      </p>
      <div className="list" style={{ marginBottom: 10 }}>
        {props.rows.map((row) => (
          <CategoryRow
            key={`${row.id}${row.label}${row.description}${row.active}`}
            row={row}
            onChanged={props.onChanged}
          />
        ))}
      </div>
      {shown < MAX_CATEGORIES
        ? (
          <form className="item-row described" onSubmit={add}>
            <input
              value={label}
              maxLength={40}
              placeholder={props.ex.category}
              aria-label="New category"
              onChange={(e) => setLabel(e.target.value)}
            />
            <input
              value={description}
              maxLength={200}
              placeholder={props.ex.categoryDescription}
              aria-label="What the new category covers"
              onChange={(e) => setDescription(e.target.value)}
            />
            <button className="small" disabled={busy || label.trim().length < 2}>Add</button>
          </form>
        )
        : (
          <p className="faint">
            {MAX_CATEGORIES} categories are shown, the most there can be. Hide one to add another.
          </p>
        )}
    </Card>
  );
}

function CategoryRow(props: { row: Taxonomy; onChanged: () => Promise<void> }) {
  const { api } = useMember();
  const { busy, run } = useAction();
  const [label, setLabel] = useState(props.row.label);
  const [description, setDescription] = useState(props.row.description ?? "");
  const other = props.row.category === "other";
  const dirty = label !== props.row.label || description !== (props.row.description ?? "");
  const save = async (patch: { label?: string; description?: string | null; active?: boolean }, done: string) => {
    if (await run(() => api.saveObjectionCategory(props.row.id, patch), done)) await props.onChanged();
  };

  return (
    <div className={`item-row described list-item${props.row.active ? "" : " hidden-row"}`}>
      <input
        value={label}
        maxLength={40}
        aria-label={`Name of ${props.row.label}`}
        onChange={(e) => setLabel(e.target.value)}
      />
      <input
        value={description}
        maxLength={200}
        placeholder="What it covers"
        aria-label={`What ${props.row.label} covers`}
        onChange={(e) => setDescription(e.target.value)}
      />
      <span className="row">
        {dirty && (
          <button
            className="primary small"
            disabled={busy || label.trim().length < 2}
            onClick={() =>
              void save({ label: label.trim(), description: description.trim() || null }, "Category saved")}
          >
            Save
          </button>
        )}
        {other ? <span className="faint" title="Objections that fit no other category">Always shown</span> : (
          <button
            className="small ghost"
            disabled={busy}
            onClick={() =>
              void save({ active: !props.row.active }, props.row.active ? "Category hidden" : "Category shown")}
          >
            {props.row.active ? "Hide" : "Show"}
          </button>
        )}
      </span>
    </div>
  );
}
